import React from "react"
import styled from "styled-components"
import { graphql } from "gatsby"

import Layout from "../layouts/Layout"
import { Container, Row, Col } from "../components/Grid"
import Snippet from "../components/Snippet"
import Spacer from "../components/Spacer"
import PageHeader from "../components/PageHeader"
import NewsletterLightbox from "../components/NewsletterLightbox"
import StyledTeaser from "../components/Teaser"
import Seo from "../components/Seo"

import { media } from "../styles"

const HeaderWrapper = styled.div`
  background: ${({ theme }) => theme.greyLight};
  margin: 0 0 20px;
  padding: 45px 15px;
  position: relative;

  @media ${media.md} {
    margin: 0 0 20px;
  }

  @media ${media.lg} {
    padding: 60px 0 65px;
  }

  @media ${media.xl} {
    padding: 90px 0 65px;
  }
`

const Grid = styled.div`
  align-items: flex-start;
  display: flex;
  flex-flow: column nowrap;
  gap: 100px;
  margin: 0;

  @media ${media.md} {
    flex-flow: row wrap;
    gap: 50px;
  }

  @media ${media.lg} {
    gap: 100px;
    padding: 0 80px;
  }
`

const Teaser = styled(StyledTeaser)`
  flex: 0 0 calc(50% - 25px);

  @media ${media.lg} {
    flex: 0 0 calc(50% - 50px);
  }
`

export const publicationTeaserQuery = graphql`
  fragment PublicationTeaserQuery on ContentfulPublication {
    id
    title
    slug
    intro {
      childMarkdownRemark {
        excerpt
      }
    }
    shortDescription {
      shortDescription
    }
    type
  }
`

export const publicationRelatedQuery = graphql`
  fragment PublicationRelatedQuery on ContentfulPublication {
    ...PublicationTeaserQuery
    image: heroImage {
      ...ContentfulImageData
      gatsbyImageData(
        height: 692
        layout: CONSTRAINED
        quality: 90
        width: 1144
      )
    }
  }
`

export const publicationQuery = graphql`
  fragment PublicationQuery on ContentfulPublication {
    id
    title
    slug
    subheader
    coverImage {
      ...ContentfulImageData
      gatsbyImageData(
        height: 1426
        layout: CONSTRAINED
        quality: 90
        width: 1008
      )
    }
    socialShareImageOpengraph: heroImage {
      ...ContentfulImageData
      gatsbyImageData(
        layout: FIXED
        width: 1200
        height: 628
        quality: 85
        formats: JPG
        resizingBehavior: THUMB
      )
    }
    socialShareImageTwitter: heroImage {
      ...ContentfulImageData
      gatsbyImageData(
        layout: FIXED
        width: 1024
        height: 512
        quality: 85
        formats: JPG
        resizingBehavior: THUMB
      )
    }
    intro {
      childMarkdownRemark {
        html
      }
    }
    publicationId
    socialShareText
    type
    testimonials {
      authorName
      authorPhoto {
        ...ContentfulImageData
        gatsbyImageData(
          cropFocus: FACE
          height: 240
          layout: CONSTRAINED
          quality: 90
          resizingBehavior: THUMB
          width: 240
        )
      }
      authorTitle
      text {
        text
      }
    }
    ctaHeader
    externalUrl
    download {
      publicUrl
      file {
        contentType
        url
        details {
          size
        }
      }
    }
  }
`

export const pageQuery = graphql`
  query PublicationsPageQuery($language: String, $originalPath: String) {
    contentfulPageMetadata(
      pagePath: { eq: $originalPath }
      node_locale: { eq: $language }
    ) {
      ...PageMetadata
    }
    allContentfulPublication(
      filter: {
        node_locale: { eq: $language }
        pageGeneration: { regex: "/custom|simple/" }
        slug: { ne: "" }
      }
      sort: { date: DESC }
    ) {
      nodes {
        ...PublicationTeaserQuery
        image: heroImage {
          ...ContentfulImageData
          gatsbyImageData(
            height: 692
            layout: CONSTRAINED
            quality: 90
            width: 1144
          )
        }
      }
    }
  }
`

const PublicationsPage = (props) => {
  const pageMetadata = props.data.contentfulPageMetadata
  const publications = props.data.allContentfulPublication.nodes

  return (
    <Layout pageId="publications">
      <Seo {...pageMetadata} />
      <Container>
        <HeaderWrapper>
          <PageHeader
            subHeader={Snippet({
              id: "publicationsSubheader",
              field: "header",
            })}
            header={Snippet({ id: "publicationsHeader", field: "header" })}
          />
        </HeaderWrapper>
        <Spacer height={20} lg={80} />
        <Grid>
          {publications.map((publication) => (
            <Teaser
              key={publication.slug}
              to={`/publications/${publication.slug}/`}
              image={publication.image}
              header={publication.title}
              text={
                publication.shortDescription?.shortDescription ||
                publication.intro?.childMarkdownRemark?.excerpt
              }
            />
          ))}
        </Grid>
        <Spacer height={20} md={100} />
        <Row>
          <Col lg="10" lgOffset="1" xl="8" xlOffset="2">
            <NewsletterLightbox />
          </Col>
        </Row>
        <Spacer height={20} md={100} />
      </Container>
    </Layout>
  )
}

export default PublicationsPage
